import React, { useState, useEffect } from 'react';
import { UserRole } from '../../user/user.type';
import logo from '../../media/logo.png';
import classNames from 'classnames';
import PositionDescription from '../../ui/position-description.ui';
import FounderDescriptionUi from '../../ui/founder-description.ui';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useJobRole } from '../../context/job-role.context';

import styles from './onboarding-steps.module.css';

enum OnboardingStepTitle {
  Phone = 'Enter your phone number',
  PersonalInfo = 'Create Profile',
  RecentProject = 'Tell me about a recent tech project you have worked on',
  RecentProjectFounder = 'Tell us about your startup',
  Skills = 'Describe yourself professionally',
  RolePreferences = 'What roles are you interested in?',
  RolePreferencesFounder = 'What are you looking for?',
  UploadDocuments = 'Please share your CV or LinkedIn',
  UploadDocumentsForRole = 'Please share your CV and LinkedIn',
  PTest = 'Personality Quiz',
}

const stepTitles = [
  OnboardingStepTitle.Phone,
  OnboardingStepTitle.PersonalInfo,
  OnboardingStepTitle.RecentProject,
  OnboardingStepTitle.Skills,
  OnboardingStepTitle.RolePreferences,
  OnboardingStepTitle.UploadDocuments,
  OnboardingStepTitle.PTest,
];

const getStepTitle = (index: number, userRole?: string, isApplied?: boolean): string => {
  const baseTitle = stepTitles[index];

  if (index === 2 && userRole === UserRole.Founder) {
    return OnboardingStepTitle.RecentProjectFounder;
  }

  if (index === 4 && userRole === UserRole.Founder) {
    return OnboardingStepTitle.RolePreferencesFounder;
  }

  if (index === 5) {
    if (isApplied) {
      return OnboardingStepTitle.UploadDocumentsForRole;
    }
  }

  return baseTitle || '';
};

export type GetPage = () => React.ReactNode;

type OnboardingStepsUiProps = {
  returnedUser: boolean,
  pages: GetPage[],
  index: number,
  progress: number | null,
  userRole?: string,
  isAppliedForPosition?: boolean | undefined,
  onContinue: () => void,
  onBack?: () => void,
  onSignOut?: () => void;
};

function OnboardingStepsUi(props: OnboardingStepsUiProps) {
  const {
    returnedUser,
    pages,
    index,
    progress,
    userRole,
    isAppliedForPosition,
    onContinue,
    onBack,
    onSignOut,
  } = props;

  const { jobRoleData } = useJobRole();
  const [currentIndex, setCurrentIndex] = useState(index);
  const currentTitle = getStepTitle(currentIndex, userRole, isAppliedForPosition);
  const [dialogOpen, setDialogOpen] = useState(returnedUser);
  const [isRightColumnOpen, setIsRightColumnOpen] = useState(true);

  useEffect(() => {
    setCurrentIndex(index);
  }, [index, currentIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onContinue();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onContinue]);

  const toggleDialog = () => {
    setDialogOpen(prev => !prev);
  };

  const onToggleRightColumn = () => {
    setIsRightColumnOpen(prev => !prev);
  };

  const jobRoleTitle = userRole === UserRole.Founder
    ? 'Welcome to Foundermatcha - Your Journey Starts Here'
    : userRole === UserRole.SoftwareEngineer
      ? (jobRoleData?.jobTitle || 'Senior Developer / CTO')
      : '';

  return (
    <>
      {returnedUser && dialogOpen && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogTitle}>Welcome back!</div>
            <div className={styles.dialogText}>
              We still need some more information from you to complete your application.
              <br />
              Are you ready to continue?
            </div>
            <button
              className={styles.dialogButton}
              onClick={toggleDialog}
              autoFocus
            >
              Let's continue
            </button>
          </div>
        </div>
      )}

      <div className={styles.layout}>
        <button
          type="button"
          className={styles.signOutButton}
          onClick={onSignOut}
        >
          Sign Out
        </button>

        <div className={styles.pageWrapper}>
          <div className={classNames(styles.leftColumn, {
            [styles.leftColumnExpanded]: !isRightColumnOpen,
          })}>
            <div className={styles.logoWrapper}>
              <img
                src={logo}
                alt="Foundermatcha Logo"
                className={styles.logo}
              />
            </div>
            <div className={styles.container}>
              <div className={styles.positionWrapper}>
                <div className={styles.title}>
                  {jobRoleTitle}
                </div>
                {userRole === UserRole.SoftwareEngineer && (
                  <div className={styles.subtitle}>
                    <LocationOnIcon fontSize="small" />
                    <div className={styles.subtext}>
                      {jobRoleData ? `${jobRoleData.location} | ${jobRoleData.workStyle} | ${jobRoleData.commitment}` : 'UK London | Hybrid | Full-time'}
                    </div>
                  </div>
                )}
              </div>
              <div
                className={classNames(styles.progressBar, {
                  [styles.hidden]: progress === null,
                })}
              >
                <div
                  className={styles.progressBarInner}
                  style={{
                    width: `${(progress || 0) * 100}%`,
                  }}
                />
              </div>
              <div className={styles.header}>
                <div className={styles.title}>{currentTitle}</div>
              </div>
              <div className={styles.form}>
                <div className={styles.contentWrapper}>
                  <div className={styles.page}>
                    {pages[currentIndex]()}
                  </div>

                  <div className={styles.footer}>
                    {onBack ? (
                      <button
                        className={styles.backButton}
                        type="button"
                        onClick={onBack}
                        disabled={index === 0}
                      >
                        Back
                      </button>
                    ) : null}

                    <button
                      className={styles.button}
                      type="button"
                      onClick={onContinue}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <>
            <div className={classNames(styles.hider, {
              [styles.hiderClosed]: !isRightColumnOpen,
            })} onClick={onToggleRightColumn}>
              {isRightColumnOpen ? (
                <ChevronRightIcon sx={{ color: 'var(--secondary-color)' }}/>
              ) : (
                <ChevronLeftIcon sx={{ color: 'var(--secondary-color)' }}/>
              )}
            </div>
            {isRightColumnOpen ? (
              <div className={styles.rightColumn}>
                <div className={styles.rightColumnInner}>
                  {userRole === UserRole.Founder ? (
                    <FounderDescriptionUi />
                  ) : (
                    <PositionDescription roleData={jobRoleData} />
                  )}
                </div>
              </div>
            ) : null}
          </>
        </div>
      </div>
    </>
  );
}

export default OnboardingStepsUi;
